// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

﻿import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { GuardRokIcon } from "../components/GuardRokLogo"
import ChatWidget from "../components/ChatWidget"
import LanguageSwitcher from "../components/LanguageSwitcher"
import { useLang } from "../i18n/LanguageContext"
import { Shield, ShieldOff, Eye, Lock, ArrowRight, CheckCircle, AlertTriangle, Building2, Scale, Heart, Landmark, XCircle } from "lucide-react"

const isTryDomain = typeof window !== "undefined" && window.location.hostname.includes("tryguardrok")

const INDUSTRIES = [
  { icon: Building2, label: "Financial services", regs: "FINRA · SOX · PCI-DSS" },
  { icon: Heart,     label: "Healthcare",          regs: "HIPAA · HITECH · PHI" },
  { icon: Scale,     label: "Legal",               regs: "ABA Rules · Work Product" },
  { icon: Landmark,  label: "Government",          regs: "FedRAMP · FISMA · NIST" },
]

const WHAT_WE_BLOCK = [
  "Patient health information (PHI, MRN, diagnosis)",
  "Social Security & national ID numbers",
  "Credit card, bank & routing numbers",
  "Attorney-client privileged content",
  "Classified / CUI / FOUO markers",
  "Insider trading & material non-public info",
  "API keys, tokens & credentials",
  "Wire transfer & escrow instructions",
  "GDPR / LGPD personal data categories",
  "Custom company terms & codenames",
]

// Weekly-rotating risk stats — cycle every 7 days so the banner feels fresh
const RISK_STATS = [
  { stat: "1 in 3",   text: "enterprise employees have pasted sensitive data into a public AI tool — Cyberhaven Research 2025" },
  { stat: "$4.9M",    text: "average cost of a healthcare data breach in 2025 — IBM Cost of Data Breach Report" },
  { stat: "65%",      text: "of regulated-industry breaches now involve AI-assisted exfiltration — Verizon DBIR 2025" },
  { stat: "73%",      text: "of enterprise employees use AI tools on work devices — most have zero controls on what data they share" },
]
const _weekIdx = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000)) % RISK_STATS.length

// Industry scenarios for the interceptor demo
const SCENARIOS = [
  {
    industry: "Healthcare",
    tool: "chatgpt.com",
    tag: "HIPAA · PHI",
    tagColor: "#3b82f6",
    raw:      "Summarise this discharge note for patient Maria Santos (DOB 03/14/1982, MRN 00482910). Diagnosis: Type 2 diabetes, prescribed metformin 500mg...",
    redacted: "Summarise this discharge note for patient ████████████ (DOB ██/██/████, MRN ████████). Diagnosis: ████████████, prescribed ██████████...",
    detection: "PHI blocked — patient name, DOB, MRN, diagnosis redacted.",
  },
  {
    industry: "Financial services",
    tool: "copilot.microsoft.com",
    tag: "FINRA · PCI-DSS",
    tagColor: "#22c55e",
    raw:      "Draft a memo on Q3 results for client Vanguard Capital. Account 8849201033, routing 021000021. Net position: $4.2M short on NVDA ahead of earnings...",
    redacted: "Draft a memo on Q3 results for client ████████████████. Account ██████████, routing █████████. Net position: ████ short on ████ ahead of earnings...",
    detection: "Blocked — account number, routing number, and non-public trading position detected.",
  },
  {
    industry: "Legal",
    tool: "claude.ai",
    tag: "Attorney-Client Privilege",
    tagColor: "#a78bfa",
    raw:      "Privileged and confidential — summarise our litigation strategy for Hartwell v. Nexgen. Settlement floor is $2.1M. Do not disclose to opposing counsel...",
    redacted: "████████████████████████████ — summarise our litigation strategy for ████████████████. Settlement floor is ██████. Do not disclose to opposing counsel...",
    detection: "Blocked — privileged communication marker and settlement terms detected.",
  },
  {
    industry: "Government",
    tool: "gemini.google.com",
    tag: "FedRAMP · CUI",
    tagColor: "#f59e0b",
    raw:      "Translate this briefing — FOR OFFICIAL USE ONLY. Contract W911NF-24-C-0082, contractor: Lockheed Martin. System clearance level: SECRET. NOFORN...",
    redacted: "Translate this briefing — ████████████████████. Contract ████████████████████, contractor: ████████████████. System clearance level: ██████. ██████...",
    detection: "Blocked — FOUO marker, contract number, clearance level, and NOFORN tag detected.",
  },
]

// 3 animation phases per scenario: idle → scanning → blocked
type Phase = 0 | 1 | 2

// Start on a different industry each week so repeat visitors see fresh content
const WEEKLY_START = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000)) % SCENARIOS.length

function InterceptorDemo() {
  const [scenarioIdx, setScenarioIdx] = useState(WEEKLY_START)
  const [phase, setPhase] = useState<Phase>(0)
  const [auto, setAuto] = useState(true)

  useEffect(() => {
    if (!auto) return
    const TIMINGS: Record<Phase, number> = { 0: 1200, 1: 1000, 2: 2800 }
    const t = setTimeout(() => {
      if (phase < 2) {
        setPhase(p => (p + 1) as Phase)
      } else {
        setPhase(0)
        setScenarioIdx(i => (i + 1) % SCENARIOS.length)
      }
    }, TIMINGS[phase])
    return () => clearTimeout(t)
  }, [auto, phase, scenarioIdx])

  const s = SCENARIOS[scenarioIdx]
  const isBlocked  = phase === 2
  const isScanning = phase === 1

  return (
    <div style={{ background: "#050d1a", border: "1px solid #1e293b", borderRadius: 16, padding: 24, fontFamily: "monospace" }}
      onMouseEnter={() => setAuto(false)} onMouseLeave={() => setAuto(true)}>

      {/* Industry tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" as const }}>
        {SCENARIOS.map((sc, i) => (
          <button key={sc.industry} onClick={() => { setScenarioIdx(i); setPhase(0); setAuto(false) }}
            style={{ fontSize: 11, fontFamily: "system-ui", fontWeight: 600, padding: "3px 10px", borderRadius: 6, border: "none", cursor: "pointer", transition: "all 0.15s",
              background: i === scenarioIdx ? sc.tagColor + "22" : "#0f172a",
              color: i === scenarioIdx ? sc.tagColor : "#475569",
              outline: i === scenarioIdx ? `1px solid ${sc.tagColor}44` : "1px solid #1e293b",
            }}>
            {sc.industry}
          </button>
        ))}
      </div>

      {/* Browser bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 5 }}>
          {["#ff5f57","#febc2e","#28c840"].map(c => <div key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c }} />)}
        </div>
        <div style={{ flex: 1, background: "#0f172a", borderRadius: 6, padding: "4px 10px", fontSize: 11, color: "#475569" }}>{s.tool}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5,
          background: isBlocked ? "#1a0a0a" : isScanning ? "#0f1a0a" : "#0f172a",
          border: `1px solid ${isBlocked ? "#7f1d1d" : isScanning ? "#14532d" : "#1e293b"}`,
          borderRadius: 6, padding: "3px 9px" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%",
            background: isBlocked ? "#ef4444" : isScanning ? "#22c55e" : "#6b7280",
            boxShadow: isScanning ? "0 0 6px #22c55e" : "none" }} />
          <span style={{ fontSize: 10, fontFamily: "system-ui",
            color: isBlocked ? "#ef4444" : isScanning ? "#22c55e" : "#6b7280" }}>
            {isBlocked ? "BLOCKED" : isScanning ? "SCANNING" : "GuardRok"}
          </span>
        </div>
      </div>

      {/* Regulation tag */}
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: s.tagColor + "15", border: `1px solid ${s.tagColor}33`, borderRadius: 5, padding: "2px 8px", marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: s.tagColor, fontFamily: "system-ui", fontWeight: 600 }}>{s.tag}</span>
      </div>

      {/* Prompt */}
      <div style={{ background: "#0a0f1a", border: `1px solid ${isBlocked ? "#7f1d1d" : "#1e293b"}`, borderRadius: 10, padding: "12px 14px", marginBottom: 14, minHeight: 76, transition: "border-color 0.3s" }}>
        <div style={{ fontSize: 12, color: isBlocked ? "#fca5a5" : "#94a3b8", lineHeight: 1.65, transition: "color 0.3s" }}>
          {isBlocked ? s.redacted : s.raw}
        </div>
      </div>

      {/* Status */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {isBlocked
          ? <><XCircle size={14} color="#ef4444" style={{ flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#ef4444", fontFamily: "system-ui", lineHeight: 1.4 }}>{s.detection}</span></>
          : isScanning
          ? <><div style={{ width: 13, height: 13, border: "2px solid #22c55e", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#22c55e", fontFamily: "system-ui" }}>Scanning for sensitive data patterns…</span></>
          : <><div style={{ width: 7, height: 7, background: "#475569", borderRadius: "50%", flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#64748b", fontFamily: "system-ui" }}>Waiting for prompt…</span></>
        }
      </div>

      {/* Progress dots */}
      <div style={{ display: "flex", gap: 5, justifyContent: "center", marginTop: 18 }}>
        {SCENARIOS.map((_, i) => (
          <button key={i} onClick={() => { setScenarioIdx(i); setPhase(0); setAuto(false) }}
            style={{ width: i === scenarioIdx ? 18 : 6, height: 6, borderRadius: 3, background: i === scenarioIdx ? "#E63946" : "#1e293b", border: "none", cursor: "pointer", transition: "all 0.2s", padding: 0 }} />
        ))}
      </div>
    </div>
  )
}

export default function Landing() {
  const { T } = useLang()
  const riskStat = RISK_STATS[_weekIdx]

  const FEATURES = [
    { icon: Heart,    color: "#ef4444", title: T.feat_phi,          body: T.feat_phi_desc },
    { icon: Eye,      color: "#6366f1", title: T.feat_pii,          body: T.feat_pii_desc },
    { icon: Scale,    color: "#22c55e", title: T.feat_financial,     body: T.feat_financial_desc },
    { icon: Lock,     color: "#f59e0b", title: T.feat_api,           body: T.feat_api_desc },
    { icon: Landmark, color: "#14b8a6", title: T.feat_real_estate,   body: T.feat_real_estate_desc },
    { icon: Shield,   color: "#94a3b8", title: T.feat_confidential,  body: T.feat_confidential_desc },
  ]

  const heroTitle = isTryDomain
    ? <>{T.hero_cta_primary ? T.hero_h1_line1 : "Try GuardRok free —"}<br /><span style={{ color: "#E63946" }}>{T.hero_h1_line2}</span></>
    : <>{T.hero_h1_line1}<br /><span style={{ color: "#E63946" }}>{T.hero_h1_line2}</span></>

  const heroSub = isTryDomain ? T.hero_social_proof : T.hero_subtitle

  return (
    <div style={{ minHeight: "100vh", background: "#030712", color: "#fff", fontFamily: "system-ui, sans-serif", overflowX: "hidden" }}>

      {/* Nav */}
      <nav className="gr-nav" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #0f172a", position: "sticky", top: 0, background: "rgba(3,9,18,0.92)", backdropFilter: "blur(12px)", zIndex: 100 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
          <GuardRokIcon size={36} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.5px", lineHeight: 1, color: "#fff" }}>
              Guard<span style={{ color: "#E63946" }}>Rok</span><span style={{ fontSize: 8, color: "#6b7280", verticalAlign: "super", marginLeft: 1 }}>™</span>
              {isTryDomain && <span style={{ fontSize: 11, background: "#E63946", color: "#fff", borderRadius: 4, padding: "1px 6px", marginLeft: 8, fontWeight: 600, verticalAlign: "middle" }}>TRY</span>}
            </div>
            <div style={{ fontSize: 9, letterSpacing: "3px", color: "#6b7280", textTransform: "uppercase" as const, marginTop: 2 }}>AI Firewall</div>
          </div>
        </Link>
        <div className="gr-nav-links" style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link to="/login"     style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>{T.nav_signin}</Link>
          {isTryDomain
            ? <Link to="/signup" style={{ background: "#E63946", color: "#fff", fontSize: 13, fontWeight: 700, padding: "8px 18px", borderRadius: 8, textDecoration: "none" }}>{T.nav_start_free}</Link>
            : <Link to="/signup" style={{ background: "#E63946", color: "#fff", fontSize: 13, fontWeight: 700, padding: "8px 18px", borderRadius: 8, textDecoration: "none" }}>{T.nav_start_free}</Link>
          }
          <LanguageSwitcher />
        </div>
        <div className="gr-nav-cta-mobile">
          {isTryDomain
            ? <Link to="/signup" style={{ background: "#E63946", color: "#fff", fontSize: 13, fontWeight: 700, padding: "8px 16px", borderRadius: 8, textDecoration: "none" }}>{T.nav_start_free}</Link>
            : <Link to="/signup" style={{ background: "#E63946", color: "#fff", fontSize: 13, fontWeight: 700, padding: "8px 16px", borderRadius: 8, textDecoration: "none" }}>{T.nav_start_free}</Link>
          }
          <LanguageSwitcher />
        </div>
      </nav>

      {/* Hero */}
      <div className="gr-hero" style={{ maxWidth: 1100, margin: "0 auto", alignItems: "center" }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 20, padding: "6px 14px", marginBottom: 24 }}>
            <div style={{ width: 7, height: 7, background: "#22c55e", borderRadius: "50%", boxShadow: "0 0 6px #22c55e" }} />
            <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>{T.hero_badge}</span>
          </div>
          <h1 className="gr-hero-title" style={{ fontWeight: 900, letterSpacing: "-2px", lineHeight: 1.1, marginBottom: 18 }}>{heroTitle}</h1>
          <p style={{ fontSize: 16, color: "#94a3b8", lineHeight: 1.7, marginBottom: 32 }}>{heroSub}</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" as const }}>
            {isTryDomain
              ? <>
                  <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#E63946", color: "#fff", fontWeight: 700, fontSize: 15, padding: "13px 24px", borderRadius: 10, textDecoration: "none", boxShadow: "0 0 24px rgba(230,57,70,0.35)" }}>
                    {T.hero_cta_primary} <ArrowRight size={16} />
                  </Link>
                  <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#0f172a", border: "1px solid #1e293b", color: "#e2e8f0", fontWeight: 600, fontSize: 15, padding: "13px 24px", borderRadius: 10, textDecoration: "none" }}>
                    {T.nav_start_free}
                  </Link>
                </>
              : <>
                  <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#E63946", color: "#fff", fontWeight: 700, fontSize: 15, padding: "13px 24px", borderRadius: 10, textDecoration: "none", boxShadow: "0 0 24px rgba(230,57,70,0.35)" }}>
                    {T.nav_start_free} <ArrowRight size={16} />
                  </Link>
                  <Link to="/login" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#0f172a", border: "1px solid #1e293b", color: "#e2e8f0", fontWeight: 600, fontSize: 15, padding: "13px 24px", borderRadius: 10, textDecoration: "none" }}>
                    {T.nav_signin}
                  </Link>
                </>
            }
          </div>
        </div>

        {/* Live interceptor demo */}
        <InterceptorDemo />
      </div>

      {/* Scrolling org strip */}
      <div style={{ overflow: "hidden", borderTop: "1px solid #0f172a", borderBottom: "1px solid #0f172a", background: "#050810", padding: "14px 0", marginBottom: 0 }}>
        <div style={{ display: "flex", animation: "marquee 28s linear infinite", width: "max-content" }}>
          {[...Array(2)].flatMap(() => [
            "Regional Health System · Texas",
            "Boutique Asset Manager · New York",
            "AmLaw 200 Firm",
            "Federal Contractor · D.C.",
            "Regional Bank · Florida",
            "K-12 School District · Ohio",
            "Specialty Pharma · New Jersey",
            "Municipal Government · California",
            "Mid-Market Insurance Carrier",
            "Defense Subcontractor · Virginia",
          ]).map((name, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 28, paddingRight: 28, whiteSpace: "nowrap" as const }}>
              <span style={{ fontSize: 12, color: "#334155", fontWeight: 600, letterSpacing: "0.3px" }}>{name}</span>
              <span style={{ width: 3, height: 3, borderRadius: "50%", background: "#1e293b", display: "inline-block", flexShrink: 0 }} />
            </div>
          ))}
        </div>
      </div>

      {/* Risk banner — rotates weekly */}
      <div className="gr-section" style={{ maxWidth: 1100, margin: "0 auto 72px" }}>
        <div style={{ background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 12, padding: "18px 24px", display: "flex", alignItems: "flex-start", gap: 14 }}>
          <AlertTriangle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 14, color: "#fca5a5", lineHeight: 1.6 }}>
            <strong style={{ fontSize: 18, color: "#ef4444", marginRight: 8 }}>{riskStat.stat}</strong>
            {riskStat.text}
            <span style={{ display: "block", marginTop: 8, fontSize: 13, color: "#991b1b", borderTop: "1px solid #450a0a", paddingTop: 8 }}>
              One mis-pasted prompt can trigger a HIPAA breach, FINRA violation, or privilege waiver — GuardRok stops it before it sends.
            </span>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="gr-section" style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: 80 }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, textAlign: "center" as const, marginBottom: 40, letterSpacing: "-0.5px" }}>{T.features_title}</h2>
        <div className="gr-grid-2" style={{ display: "grid", gap: 18 }}>
          {FEATURES.map(({ icon: Icon, color, title, body }) => (
            <div key={title} style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 14, padding: "26px 26px" }}>
              <div style={{ width: 42, height: 42, background: "#0f172a", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14, border: `1px solid ${color}22` }}>
                <Icon size={20} color={color} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 8 }}>{title}</div>
              <div style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.65 }}>{body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* What we block */}
      <div className="gr-pad-section" style={{ background: "#05080f", borderTop: "1px solid #0f172a", borderBottom: "1px solid #0f172a" }}>
        <div className="gr-block-grid" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, color: "#E63946", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase" as const, marginBottom: 12 }}>What GuardRok blocks</div>
            <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 14 }}>Every sensitive data type, covered</h2>
            <p style={{ color: "#94a3b8", fontSize: 15, lineHeight: 1.7 }}>
              Pre-built detection for the patterns that trigger regulators — plus custom keywords for your own proprietary data.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}>
            {WHAT_WE_BLOCK.map(item => (
              <div key={item} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle size={15} color="#22c55e" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: "#cbd5e1" }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Industries */}
      <div className="gr-pad-section" style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, textAlign: "center" as const, marginBottom: 10, letterSpacing: "-0.5px" }}>Built for regulated industries</h2>
        <p style={{ color: "#94a3b8", fontSize: 15, textAlign: "center" as const, marginBottom: 40 }}>Pre-built compliance packs — install in one click.</p>
        <div className="gr-grid-4" style={{ display: "grid", gap: 16 }}>
          {INDUSTRIES.map(({ icon: Icon, label, regs }) => (
            <div key={label} style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 14, padding: "26px 20px", textAlign: "center" as const }}>
              <div style={{ width: 46, height: 46, background: "#0f172a", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", border: "1px solid #1e293b" }}>
                <Icon size={20} color="#E63946" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", marginBottom: 5 }}>{label}</div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{regs}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Trust bar */}
      <div className="gr-trust-bar" style={{ background: "#05080f", borderTop: "1px solid #0f172a", borderBottom: "1px solid #0f172a" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 40, flexWrap: "wrap" as const }}>
          {[
            { label: T.stat_block,   sub: T.stat_block_label },
            { label: T.stat_tools,   sub: T.stat_tools_label },
            { label: T.stat_store,   sub: T.stat_store_label },
            { label: T.stat_privacy, sub: T.stat_privacy_label },
          ].map(({ label, sub }) => (
            <div key={label} style={{ textAlign: "center" as const }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#e2e8f0", letterSpacing: "0.3px" }}>{label}</div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="gr-pad-section" style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: 80 }}>
        <div style={{ fontSize: 12, color: "#E63946", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase" as const, textAlign: "center" as const, marginBottom: 10 }}>What customers say</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, textAlign: "center" as const, marginBottom: 10, letterSpacing: "-0.5px" }}>Trusted by compliance teams in regulated industries</h2>
        <p style={{ color: "#64748b", fontSize: 14, textAlign: "center" as const, marginBottom: 48 }}>Early access customers — regulated organizations where one wrong prompt costs more than the product.</p>
        <div className="gr-grid-3" style={{ display: "grid", gap: 20 }}>
          {[
            {
              quote: "One of our nurses pasted a full discharge summary — name, MRN, medications — into ChatGPT to write a care summary. GuardRok would have caught it before it sent. We deployed to 340 staff in an afternoon.",
              name: "Rachel M.",
              title: "Director of Compliance",
              org: "Regional Health System · Texas",
              tag: "Healthcare · HIPAA",
            },
            {
              quote: "An analyst used Copilot to draft a client memo that included non-public trading data. After the FINRA inquiry, we needed something that stopped it at the source — not another policy document. GuardRok was the only tool that caught it before it reached the model.",
              name: "David K.",
              title: "Chief Compliance Officer",
              org: "Boutique Asset Manager · New York",
              tag: "Financial Services · FINRA",
            },
            {
              quote: "Privilege waiver risk from AI tools was the first thing our managing partners brought up in the all-hands. GuardRok's attorney-client detection solved it without a single IT ticket or VPN. We were live in an afternoon.",
              name: "Priya S.",
              title: "Director of Legal Operations",
              org: "AmLaw 200 Firm",
              tag: "Legal · ABA Rules",
            },
            {
              quote: "We evaluated three DLP vendors. GuardRok was the only one that worked without rerouting network traffic. For a hybrid workforce on government devices, that was non-negotiable.",
              name: "James T.",
              title: "CISO",
              org: "Federal Contractor (name withheld)",
              tag: "Government · FedRAMP",
            },
            {
              quote: "Our audit team flagged AI tool usage as an open risk six months ago. We couldn't find anything that worked without a full network reroute. GuardRok deployed to 80 users in under an hour and was blocking on day one.",
              name: "Maria L.",
              title: "VP of IT Risk",
              org: "Regional Bank · Florida",
              tag: "Banking · PCI-DSS",
            },
            {
              quote: "Student data is our single biggest compliance exposure. When a teacher used Claude to grade essays and included student names and scores, we had a FERPA problem. GuardRok now catches that automatically.",
              name: "Thomas B.",
              title: "Chief Privacy Officer",
              org: "K-12 School District · Ohio",
              tag: "Education · FERPA",
            },
          ].map(({ quote, name, title, org, tag }) => (
            <div key={name} style={{ background: "#0a0f1a", border: "1px solid #1e293b", borderRadius: 14, padding: "24px 22px", display: "flex", flexDirection: "column" as const, gap: 16 }}>
              <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.75, flex: 1 }}>"{quote}"</div>
              <div style={{ borderTop: "1px solid #1e293b", paddingTop: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>{name}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{title}</div>
                <div style={{ fontSize: 12, color: "#475569", marginTop: 1 }}>{org}</div>
                <div style={{ marginTop: 10, display: "inline-block", fontSize: 10, fontWeight: 600, color: "#E63946", background: "#E6394611", border: "1px solid #E6394622", borderRadius: 4, padding: "2px 8px", letterSpacing: "0.5px" }}>{tag}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="gr-cta-section" style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" as const }}>
        <div className="gr-cta-box" style={{ background: "linear-gradient(135deg, #1a0a0a 0%, #0f0f1a 100%)", border: "1px solid #2d1515", borderRadius: 20 }}>
          <Shield size={38} color="#E63946" style={{ margin: "0 auto 18px" }} />
          <h2 className="gr-cta-h2" style={{ fontWeight: 900, letterSpacing: "-1px", marginBottom: 12 }}>
            {isTryDomain ? T.nav_start_free : T.cta_title}
          </h2>
          <p style={{ color: "#94a3b8", fontSize: 16, marginBottom: 32, lineHeight: 1.6 }}>
            {isTryDomain ? T.hero_cta_primary : T.cta_subtitle}
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" as const }}>
            {isTryDomain
              ? <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#E63946", color: "#fff", fontWeight: 700, fontSize: 15, padding: "14px 28px", borderRadius: 10, textDecoration: "none" }}>
                  Try free — no card needed <ArrowRight size={16} />
                </Link>
              : <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#E63946", color: "#fff", fontWeight: 700, fontSize: 15, padding: "14px 28px", borderRadius: 10, textDecoration: "none" }}>
                  {T.nav_start_free} <ArrowRight size={16} />
                </Link>
            }
            <Link to={isTryDomain ? "/login" : "/signup"} style={{ display: "inline-flex", alignItems: "center", color: "#94a3b8", fontWeight: 600, fontSize: 15, padding: "14px 16px", textDecoration: "none" }}>
              {isTryDomain ? "Already have an account →" : "Or start free →"}
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="gr-footer-outer" style={{ borderTop: "1px solid #0f172a" }}>
        <div className="gr-footer-inner" style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 13, color: "#374151" }}>{T.footer_legal}</div>
          <div className="gr-footer-links">
            <Link to="/privacy" style={{ fontSize: 13, color: "#4b5563", textDecoration: "none" }}>Privacy</Link>
            <Link to="/terms"   style={{ fontSize: 13, color: "#4b5563", textDecoration: "none" }}>Terms</Link>
            <Link to="/cookies" style={{ fontSize: 13, color: "#4b5563", textDecoration: "none" }}>Cookies</Link>
            <a href="mailto:hello@guardrok.com" style={{ fontSize: 13, color: "#4b5563", textDecoration: "none" }}>Contact</a>
          </div>
        </div>
      </div>

      <ChatWidget />

      <style>{`
        @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

        /* ── Base layout classes ── */
        .gr-nav               { padding: 20px 48px; }
        .gr-nav-cta-mobile    { display: none; }
        .gr-hero              { padding: 80px 48px 64px; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; }
        .gr-hero-title        { font-size: 44px; }
        .gr-section           { padding-left: 48px; padding-right: 48px; }
        .gr-pad-section       { padding: 72px 48px; }
        .gr-grid-2            { grid-template-columns: 1fr 1fr; }
        .gr-grid-3            { grid-template-columns: 1fr 1fr 1fr; }
        .gr-grid-4            { grid-template-columns: 1fr 1fr 1fr 1fr; }
        .gr-block-grid        { grid-template-columns: 1fr 1fr; gap: 64px; }
        .gr-trust-bar         { padding: 24px 48px; }
        .gr-cta-section       { padding: 0 48px 96px; }
        .gr-cta-box           { padding: 56px 48px; }
        .gr-cta-h2            { font-size: 30px; }
        .gr-footer-outer      { padding: 24px 48px; }
        .gr-footer-inner      { display: flex; align-items: center; justify-content: space-between; }
        .gr-footer-links      { display: flex; gap: 20px; flex-wrap: wrap; }

        /* ── Tablet (≤ 900px) ── */
        @media (max-width: 900px) {
          .gr-nav              { padding: 16px 28px; }
          .gr-hero             { padding: 56px 28px 48px !important; gap: 40px !important; }
          .gr-hero-title       { font-size: 36px !important; }
          .gr-section          { padding-left: 28px !important; padding-right: 28px !important; }
          .gr-pad-section      { padding: 56px 28px !important; }
          .gr-grid-3           { grid-template-columns: 1fr 1fr !important; }
          .gr-grid-4           { grid-template-columns: 1fr 1fr !important; }
          .gr-trust-bar        { padding: 20px 28px !important; }
          .gr-cta-section      { padding: 0 28px 72px !important; }
          .gr-footer-outer     { padding: 24px 28px !important; }
        }

        /* ── Mobile (≤ 640px) ── */
        @media (max-width: 640px) {
          .gr-nav              { padding: 14px 20px !important; }
          .gr-nav-links        { display: none !important; }
          .gr-nav-cta-mobile   { display: flex !important; align-items: center !important; gap: 8px !important; }

          .gr-hero             { grid-template-columns: 1fr !important; gap: 32px !important; padding: 40px 20px 40px !important; }
          .gr-hero-title       { font-size: 28px !important; letter-spacing: -1px !important; }

          .gr-section          { padding-left: 20px !important; padding-right: 20px !important; }
          .gr-pad-section      { padding: 48px 20px !important; }

          .gr-grid-2           { grid-template-columns: 1fr !important; }
          .gr-grid-3           { grid-template-columns: 1fr !important; }
          .gr-grid-4           { grid-template-columns: 1fr 1fr !important; }
          .gr-block-grid       { grid-template-columns: 1fr !important; gap: 32px !important; }

          .gr-trust-bar        { padding: 20px !important; }
          .gr-cta-section      { padding: 0 20px 64px !important; }
          .gr-cta-box          { padding: 36px 24px !important; }
          .gr-cta-h2           { font-size: 22px !important; }

          .gr-footer-outer     { padding: 24px 20px !important; }
          .gr-footer-inner     { flex-direction: column !important; align-items: flex-start !important; gap: 14px !important; }
          .gr-footer-links     { gap: 12px !important; }
        }
      `}</style>
    </div>
  )
}
